import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import Layout from '../components/Layout';
import ExportOtpDialog from '../components/ExportOtpDialog';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { EditOutlined as EditIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../apiBase';
import { useAuth } from '../context/AuthContext';

export default function DailyVendorFollowupReport() {
  const { user } = useAuth();
  const role = user?.role || '';
  const isSuperAdmin = role === 'superadmin';
  const permissionsMap = user?.page_permissions_map || {};
  const pagePerm = permissionsMap.vendor_followup_report || {};
  const canUpdate = isSuperAdmin || Boolean(pagePerm.can_update);
  const canDelete = isSuperAdmin || Boolean(pagePerm.can_delete);
  const canImport = isSuperAdmin || Boolean(pagePerm.can_import);
  const canExport = isSuperAdmin || Boolean(pagePerm.can_export);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [editOpen, setEditOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterFirmBank, setFilterFirmBank] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterVendorBank, setFilterVendorBank] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [pendingExportType, setPendingExportType] = useState(null);
  const SHOW_IMPORT = false;

  const loadData = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/api/vendor-followup`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const data = await res.json();
      const arr = Array.isArray(data) ? data : [];
      setRows(arr);
    } catch (e) {
      setNotification({ open: true, message: 'Failed to load records', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);



  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sd = startDate ? new Date(startDate) : null;
    const ed = endDate ? new Date(endDate) : null;
    const userAssociations = user?.association || [];

    return rows.filter((r) => {
      const dateStr = r.communication_date ? String(r.communication_date).slice(0, 10) : '';
      const d = dateStr ? new Date(dateStr) : null;
      if (sd && d && d < sd) return false;
      if (ed && d && d > ed) return false;
      if (filterUser && r.user_code !== filterUser) return false;
      if (filterFirmBank && r.from_firm_bank_code !== filterFirmBank) return false;
      if (filterVendor && r.to_vendor_code !== filterVendor) return false;
      if (filterVendorBank && r.to_vendor_bank_code !== filterVendorBank) return false;
      
      // Filter by association
      if (userAssociations.length > 0 && !userAssociations.includes(r.to_vendor_code)) return false;

      if (!q) return true;
      const fields = [
        r.user_code,
        r.from_firm_bank_code,
        r.to_vendor_code,
        r.to_vendor_bank_code,
        r.requirement,
      ];
      return fields.some((f) => String(f || '').toLowerCase().includes(q));
    });
  }, [rows, search, startDate, endDate, filterUser, filterFirmBank, filterVendor, filterVendorBank, user]);

  const uniqueOptions = useMemo(() => {
    const opts = { users: new Set(), firmBanks: new Set(), vendors: new Set(), vendorBanks: new Set() };
    const userAssociations = user?.association || [];

    rows.forEach(r => {
      if (userAssociations.length > 0 && !userAssociations.includes(r.to_vendor_code)) return;

      if (r.user_code) opts.users.add(r.user_code);
      if (r.from_firm_bank_code) opts.firmBanks.add(r.from_firm_bank_code);
      if (r.to_vendor_code) opts.vendors.add(r.to_vendor_code);
      if (r.to_vendor_bank_code) opts.vendorBanks.add(r.to_vendor_bank_code);
    });
    return {
      users: Array.from(opts.users).sort(),
      firmBanks: Array.from(opts.firmBanks).sort(),
      vendors: Array.from(opts.vendors).sort(),
      vendorBanks: Array.from(opts.vendorBanks).sort(),
    };
  }, [rows, user]);

  const parseRequirement = (v) => {
    const s = String(v ?? '').trim();
    if (!s) return 0;
    const n = Number(s.replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };
  const totalRecords = filteredRows.length;
  const totalVendorBalance = filteredRows.reduce((s, r) => s + Number(r.vendor_balance || 0), 0);
  const totalCashReceived = filteredRows.reduce((s, r) => s + Number(r.cash_received || 0), 0);
  const totalRequirement = filteredRows.reduce((s, r) => s + parseRequirement(r.requirement), 0);

  const vendorSummary = useMemo(() => {
    const map = {};
    for (const r of filteredRows) {
      const from = r.from_firm_bank_code || 'Unknown';
      const to = r.to_vendor_code || 'Unknown';
      const key = `${from} → ${to}`;
      if (!map[key]) {
        map[key] = { label: key, amount: 0, count: 0 };
      }
      map[key].amount += parseRequirement(r.requirement);
      map[key].count += 1;
    }
    return Object.values(map).sort((a, b) => b.amount - a.amount);
  }, [filteredRows]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const formatINR = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(n);
  const StatBox = ({ title, value, valueColor, borderColor }) => (
    <Paper sx={{ p: 3, height: 120, borderRadius: 3, border: '2px solid', borderColor }}>
      <Typography align="center" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography align="center" sx={{ mt: 1, fontWeight: 700, fontSize: 24, color: valueColor }}>
        {value}
      </Typography>
    </Paper>
  );

  const handleEdit = (row) => {
    if (!canUpdate) {
      setNotification({ open: true, message: 'You do not have update permission for this page', severity: 'error' });
      return;
    }
    setCurrent({
      id: row.id,
      userCode: row.user_code || '',
      date: row.communication_date ? String(row.communication_date).slice(0, 10) : '',
      fromFirmBankCode: row.from_firm_bank_code || '',
      toVendorCode: row.to_vendor_code || '',
      toVendorBankCode: row.to_vendor_bank_code || '',
      requirement: row.requirement || '',
      vendorBalance: row.vendor_balance ?? '',
      cashReceived: row.cash_received ?? '',
      adjustments: row.adjustments ?? '',
    });
    setEditOpen(true);
  };

  const requestDelete = (row) => {
    if (!canDelete) {
      setNotification({ open: true, message: 'You do not have delete permission for this page', severity: 'error' });
      return;
    }
    setConfirmRow(row);
    setConfirmOpen(true);
  };
  const performDelete = async () => {
    const row = confirmRow;
    if (!row?.id) {
      setConfirmOpen(false);
      return;
    }
    if (!canDelete) {
      setNotification({ open: true, message: 'You do not have delete permission for this page', severity: 'error' });
      setConfirmOpen(false);
      setConfirmRow(null);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/vendor-followup/${row.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      setNotification({ open: true, message: 'Record deleted successfully', severity: 'success' });
      setConfirmOpen(false);
      setConfirmRow(null);
      loadData();
    } catch (e) {
      setNotification({ open: true, message: e.message || 'Delete failed', severity: 'error' });
      setConfirmOpen(false);
      setConfirmRow(null);
    }
  };

  const handleEditSave = async () => {
    if (!current?.id) return;
    if (!canUpdate) {
      setNotification({ open: true, message: 'You do not have update permission for this page', severity: 'error' });
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/vendor-followup/${current.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify({
          userCode: current.userCode,
          date: current.date,
          fromFirmBankCode: current.fromFirmBankCode,
          toVendorCode: current.toVendorCode,
          toVendorBankCode: current.toVendorBankCode,
          requirement: current.requirement,
          vendorBalance: current.vendorBalance,
          cashReceived: current.cashReceived,
          adjustments: current.adjustments,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setNotification({ open: true, message: 'Updated', severity: 'success' });
      setEditOpen(false);
      setCurrent(null);
      loadData();
    } catch (e) {
      setNotification({ open: true, message: e.message || 'Update failed', severity: 'error' });
    }
  };

  const exportHeaders = useMemo(
    () => [
      'userCode',
      'date',
      'fromFirmBankCode',
      'toVendorCode',
      'toVendorBankCode',
      'requirement',
      'vendorBalance',
      'cashReceived',
      'adjustments',
    ],
    []
  );

  const transformForExport = () =>
    filteredRows.map((r) => ({
      userCode: r.user_code || '',
      date: r.communication_date ? String(r.communication_date).slice(0, 10) : '',
      fromFirmBankCode: r.from_firm_bank_code || '',
      toVendorCode: r.to_vendor_code || '',
      toVendorBankCode: r.to_vendor_bank_code || '',
      requirement: r.requirement || '',
      vendorBalance: r.vendor_balance ?? '',
      cashReceived: r.cash_received ?? '',
      adjustments: r.adjustments ?? '',
    }));

  const performExportExcel = async () => {
    const data = transformForExport();
    const ws = XLSX.utils.json_to_sheet(data, { header: exportHeaders });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'DailyVendorFollowup');

    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: 'dailyvendorfollowup.xlsx',
          types: [{
            description: 'Excel File',
            accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
          }],
        });
        const writable = await handle.createWritable();
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        await writable.write(buf);
        await writable.close();
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('File picker error, falling back to download', err);
      }
    }

    XLSX.writeFile(wb, 'dailyvendorfollowup.xlsx');
  };

  const performExportCsv = async () => {
    const data = transformForExport();
    const ws = XLSX.utils.json_to_sheet(data, { header: exportHeaders });
    const csv = XLSX.utils.sheet_to_csv(ws);

    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: 'dailyvendorfollowup.csv',
          types: [{
            description: 'CSV File',
            accept: { 'text/csv': ['.csv'] },
          }],
        });
        const writable = await handle.createWritable();
        await writable.write(csv);
        await writable.close();
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('File picker error, falling back to download', err);
      }
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dailyvendorfollowup.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = () => {
    if (!canExport) {
      setNotification({ open: true, message: 'You do not have export permission for this page', severity: 'error' });
      return;
    }
    if (isSuperAdmin) {
      performExportExcel();
    } else {
      setPendingExportType('excel');
      setOtpDialogOpen(true);
    }
  };

  const handleExportCsv = () => {
    if (!canExport) {
      setNotification({ open: true, message: 'You do not have export permission for this page', severity: 'error' });
      return;
    }
    if (isSuperAdmin) {
      performExportCsv();
    } else {
      setPendingExportType('csv');
      setOtpDialogOpen(true);
    }
  };

  const onOtpSuccess = () => {
    if (pendingExportType === 'excel') performExportExcel();
    else if (pendingExportType === 'csv') performExportCsv();
    setPendingExportType(null);
    setOtpDialogOpen(false);
  };

  const handleDownloadTemplate = () => {
    if (!canExport) {
      setNotification({ open: true, message: 'You do not have export permission for this page', severity: 'error' });
      return;
    }
    const ws = XLSX.utils.aoa_to_sheet([exportHeaders]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'dailyvendorfollowup_template.xlsx');
  };

  const handleImportFile = async (file) => {
    if (!canImport) {
      setNotification({ open: true, message: 'You do not have import permission for this page', severity: 'error' });
      return;
    }
    setImporting(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { header: 0, raw: false, defval: '' });
      const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const buildKeyMap = (obj) => {
        const km = {};
        Object.keys(obj).forEach((k) => {
          km[norm(k)] = obj[k];
        });
        return km;
      };
      const pick = (km, variants) => {
        for (const v of variants) {
          const key = norm(v);
          const val = km[key];
          if (val !== undefined && val !== null && String(val).trim() !== '') return String(val).trim();
        }
        return '';
      };
      const normalizeDate = (v) => {
        if (!v) return '';
        if (v instanceof Date) return v.toISOString().slice(0, 10);
        const s = String(v).trim();
        if (!s) return '';
        const parts = s.includes('/') ? s.split('/') : s.split('-');
        if (parts.length === 3) {
          if (parts[0].length === 4) return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
          return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
        const n = Number(s);
        if (Number.isFinite(n) && n > 0) {
          const epoch = new Date(Date.UTC(1899, 11, 30));
          const d = new Date(epoch.getTime() + n * 24 * 60 * 60 * 1000);
          return d.toISOString().slice(0, 10);
        }
        return '';
      };
      const toNum = (v) => {
        const s = String(v ?? '').trim().replace(/[^0-9.\-]/g, '');
        const n = Number(s);
        return Number.isFinite(n) ? n : 0;
      };
      const items = json.map((r) => {
        const km = buildKeyMap(r);
        const userCode = pick(km, ['userCode', 'User Code', 'Application User Code', 'application_user_code', 'user_code', 'user']);
        const dateRaw = pick(km, ['date', 'Date', 'Date of Communication', 'date_of_communication', 'communication_date']);
        const date = normalizeDate(dateRaw);
        const toVendorCode = pick(km, ['toVendorCode', 'To Vendor Code', 'to_vendor_code', 'vendorcode', 'toVendor']);
        const fromFirmBankCode = pick(km, ['fromFirmBankCode', 'From Firm Bank Code', 'from_firm_bank_code', 'firmbankcode', 'fromBankCode']);
        const toVendorBankCode = pick(km, ['toVendorBankCode', 'To Vendor Bank Code', 'to_vendor_bank_code', 'vendorbankcode', 'toBankCode']);
        const requirement = pick(km, ['requirement', 'Requirement', 'req', 'requirements']);
        const vendorBalance = toNum(pick(km, ['vendorBalance', 'Vendor Balance', 'vendor_balance', 'vendor_balance_as_per_vendor', 'balance']));
        const cashReceived = toNum(pick(km, ['cashReceived', 'Cash Received', 'cash_received', 'cash']));
        const adjustments = toNum(pick(km, ['adjustments', 'Adjustments', 'adjustment']));
        return {
          userCode,
          date,
          fromFirmBankCode,
          toVendorCode,
          toVendorBankCode,
          requirement,
          vendorBalance,
          cashReceived,
          adjustments,
        };
      }).filter((it) => it.userCode && it.date && it.toVendorCode);
      if (items.length === 0) {
        const headers = Object.keys(json?.[0] || {});
        setNotification({
          open: true,
          severity: 'error',
          message: `No valid rows found. Ensure columns include User Code, Date, To Vendor Code. Detected headers: ${headers.join(', ')}`,
        });
        setImporting(false);
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/vendor-followup/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Import failed');
      setNotification({
        open: true,
        message: `Import success. Inserted: ${data.inserted}, Skipped: ${data.skipped}`,
        severity: 'success',
      });
      loadData();
    } catch (e) {
      setNotification({ open: true, message: e.message || 'Import failed', severity: 'error' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Layout title="Daily Vendor Followup Report">
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 500, letterSpacing: 0.5, mb: 4 }}>
          Daily Vendor Followup Report
        </Typography>

        <Paper sx={{ p: 2, borderRadius: 3, mb: 3 }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} flexWrap="wrap">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>User</InputLabel>
              <Select value={filterUser} label="User" onChange={(e) => setFilterUser(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {uniqueOptions.users.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel shrink>From Firm Bank Code</InputLabel>
              <Select
                value={filterFirmBank}
                label="From Firm Bank Code"
                onChange={(e) => setFilterFirmBank(e.target.value)}
                displayEmpty
                sx={{ '& .MuiSelect-select': { pr: 4 }, '& .MuiSelect-icon': { right: 8 } }}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {uniqueOptions.firmBanks.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>To Vendor Code</InputLabel>
              <Select value={filterVendor} label="To Vendor Code" onChange={(e) => setFilterVendor(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {uniqueOptions.vendors.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel shrink>To Vendor Bank Code</InputLabel>
              <Select
                value={filterVendorBank}
                label="To Vendor Bank Code"
                onChange={(e) => setFilterVendorBank(e.target.value)}
                displayEmpty
                sx={{ '& .MuiSelect-select': { pr: 4 }, '& .MuiSelect-icon': { right: 8 } }}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {uniqueOptions.vendorBanks.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={loadData}>Filter</Button>
            <Button variant="contained" color="inherit" onClick={() => {
              setStartDate('');
              setEndDate('');
              setFilterUser('');
              setFilterFirmBank('');
              setFilterVendor('');
              setFilterVendorBank('');
              setSearch('');
            }}>
              Reset
            </Button>
          </Stack>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2.5, mb: 3 }}>
          <StatBox title="Total Requirement" value={formatINR(totalRequirement)} valueColor="#2563eb" borderColor="rgba(37,99,235,0.35)" />
          <StatBox title="Total Vendor Balance" value={formatINR(totalVendorBalance)} valueColor="#16a34a" borderColor="rgba(22,163,74,0.35)" />
          <StatBox title="Total Cash Received" value={formatINR(totalCashReceived)} valueColor="#f59e0b" borderColor="rgba(245,158,11,0.35)" />
          <StatBox title="Total Transactions" value={String(totalRecords)} valueColor="#06b6d4" borderColor="rgba(6,182,212,0.35)" />
        </Box>

        {vendorSummary.length > 0 && (
          <Paper
            variant="outlined"
            sx={{
              mb: 3,
              overflow: 'hidden',
              borderRadius: 2,
              borderColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'),
            }}
          >
            <Box
              sx={{
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#64748b'),
                color: 'white',
                px: 2,
                py: 1.5,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Vendor to Vendor Requirement Summary
              </Typography>
            </Box>
            <Box sx={{ p: 2, bgcolor: (t) => (t.palette.mode === 'dark' ? 'transparent' : '#f8fafc') }}>
              <Grid container spacing={2}>
                {vendorSummary.map((item) => (
                  <Grid size={{ xs: 12, md: 4 }} key={item.label}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        borderColor: 'transparent',
                        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <CardContent sx={{ p: '16px !important' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5, lineHeight: 1.2 }}>
                              {item.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Transactions: {item.count}
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ color: '#2563eb', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {formatINR(item.amount)}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        )}

        <Stack direction="row" spacing={2} sx={{ mb: 2, justifyContent: 'center' }}>
          <Button variant="contained" onClick={loadData} disabled={loading}>
            Refresh
          </Button>

          <Button variant="outlined" onClick={handleExportExcel}>
            Export Excel
          </Button>
          <Button variant="outlined" onClick={handleExportCsv}>
            Export CSV
          </Button>
          {/* <Button variant="outlined" onClick={handleDownloadTemplate}>
          Download Template
          </Button> */}
          {SHOW_IMPORT && (
            <Button
              variant="contained"
              component="label"
              disabled={importing || !canImport}
            >
              Import
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImportFile(f);
                  e.target.value = '';
                }}
              />
            </Button>
          )}
        </Stack>

        <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ minWidth: 240 }}
            />
            {search && <Chip label={`Search: ${search}`} onDelete={() => setSearch('')} />}
          </Stack>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: 3, boxShadow: '0 18px 45px rgba(15,23,42,0.08)' }}>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>User Code</TableCell>
                  <TableCell>From Firm Bank Code</TableCell>
                  <TableCell>To Vendor Code</TableCell>
                  <TableCell>To Vendor Bank Code</TableCell>
                  <TableCell>Requirement</TableCell>
                  <TableCell>Vendor Balance</TableCell>
                  <TableCell>Cash Received</TableCell>
                  <TableCell>Adjustments</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.communication_date ? String(r.communication_date).slice(0, 10) : ''}</TableCell>
                    <TableCell>{r.user_code}</TableCell>
                    <TableCell>{r.from_firm_bank_code}</TableCell>
                    <TableCell>{r.to_vendor_code}</TableCell>
                    <TableCell>{r.to_vendor_bank_code}</TableCell>
                    <TableCell>{r.requirement}</TableCell>
                    <TableCell>{r.vendor_balance}</TableCell>
                    <TableCell>{r.cash_received}</TableCell>
                    <TableCell>{r.adjustments}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Edit" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(r)}
                            disabled={!canUpdate}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => requestDelete(r)}
                            disabled={!canDelete}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>



        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this record?</Typography>
            {confirmRow && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">User: {confirmRow.user_code}</Typography>
                <Typography variant="body2">Date: {confirmRow.communication_date ? String(confirmRow.communication_date).slice(0, 10) : ''}</Typography>
                <Typography variant="body2">Vendor: {confirmRow.to_vendor_code}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={performDelete}>Delete</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit Followup</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              <TextField
                label="Application User Code"
                value={current?.userCode || ''}
                onChange={(e) => setCurrent((p) => ({ ...p, userCode: e.target.value }))}
                variant="standard"
                fullWidth
              />
              <TextField
                label="Date"
                type="date"
                value={current?.date || ''}
                onChange={(e) => setCurrent((p) => ({ ...p, date: e.target.value }))}
                variant="standard"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="From Firm Bank Code"
                value={current?.fromFirmBankCode || ''}
                onChange={(e) => setCurrent((p) => ({ ...p, fromFirmBankCode: e.target.value }))}
                variant="standard"
                fullWidth
              />
              <TextField
                label="To Vendor Code"
                value={current?.toVendorCode || ''}
                onChange={(e) => setCurrent((p) => ({ ...p, toVendorCode: e.target.value }))}
                variant="standard"
                fullWidth
              />
              <TextField
                label="To Vendor Bank Code"
                value={current?.toVendorBankCode || ''}
                onChange={(e) => setCurrent((p) => ({ ...p, toVendorBankCode: e.target.value }))}
                variant="standard"
                fullWidth
              />
              <TextField
                label="Requirement"
                value={current?.requirement || ''}
                onChange={(e) => setCurrent((p) => ({ ...p, requirement: e.target.value }))}
                variant="standard"
                fullWidth
              />
              <TextField
                label="Vendor Balance"
                value={current?.vendorBalance || ''}
                onChange={(e) => setCurrent((p) => ({ ...p, vendorBalance: e.target.value }))}
                variant="standard"
                fullWidth
              />
              <TextField
                label="Cash Received"
                value={current?.cashReceived || ''}
                onChange={(e) => setCurrent((p) => ({ ...p, cashReceived: e.target.value }))}
                variant="standard"
                fullWidth
              />
              <TextField
                label="Adjustments"
                value={current?.adjustments || ''}
                onChange={(e) => setCurrent((p) => ({ ...p, adjustments: e.target.value }))}
                variant="standard"
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleEditSave}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={() => setNotification((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setNotification((p) => ({ ...p, open: false }))}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>

        <ExportOtpDialog
          open={otpDialogOpen}
          onClose={() => setOtpDialogOpen(false)}
          onSuccess={onOtpSuccess}
        />
      </Box>
    </Layout>
  );
}
